import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type {
  ApiKey,
  ApiKeyCreatedResponse,
  ApiKeyListResponse,
  AppEndpoint,
  AppEndpointListResponse,
} from '../../lib/types.js';

interface ApiKeysListLogic {
  isLoading: boolean;
  data: ApiKey[] | undefined;
  error: Error | null;
}

interface CreateApiKeyLogic {
  displayName: string;
  setDisplayName: (v: string) => void;
  scopes: string[];
  setScopes: (v: string[]) => void;
  limited: boolean;
  setLimited: (v: boolean) => void;
  pickedEndpoints: string[];
  setPickedEndpoints: (v: string[]) => void;
  isSubmitting: boolean;
  error: Error | null;
  /** `allowedEndpoints` is the final list (empty for no limit); the page filters it by scope. */
  submit: (allowedEndpoints: string[]) => Promise<ApiKeyCreatedResponse>;
  reset: () => void;
}

interface RevokeApiKeyLogic {
  isSubmitting: boolean;
  error: Error | null;
  revoke: (keyId: string) => Promise<void>;
}

interface DeleteApiKeyLogic {
  isSubmitting: boolean;
  error: Error | null;
  delete: (keyId: string) => Promise<void>;
}

export const useApiKeysListLogic = (tenantId: string | undefined): ApiKeysListLogic => {
  const query = useQuery<ApiKeyListResponse>({
    queryKey: queryKeys.apiKeys(tenantId ?? ''),
    queryFn: () => apiRequest<ApiKeyListResponse>(`/api/api-keys`),
    enabled: Boolean(tenantId),
  });

  return {
    isLoading: query.isLoading,
    data: query.data?.apiKeys,
    error: query.error,
  };
};

export const useCreateApiKeyLogic = (
  tenantId: string | undefined,
  onSuccess: (response: ApiKeyCreatedResponse) => void,
): CreateApiKeyLogic => {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [limited, setLimited] = useState(false);
  const [pickedEndpoints, setPickedEndpoints] = useState<string[]>([]);

  const mutation = useMutation<ApiKeyCreatedResponse, Error, string[]>({
    mutationFn: (allowedEndpoints) =>
      apiRequest<ApiKeyCreatedResponse>(`/api/api-keys`, {
        method: 'POST',
        body: {
          displayName: displayName.trim(),
          scopes,
          allowedEndpoints,
        },
      }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys(tenantId ?? '') });
      onSuccess(response);
    },
  });

  return {
    displayName,
    setDisplayName,
    scopes,
    setScopes,
    limited,
    setLimited,
    pickedEndpoints,
    setPickedEndpoints,
    isSubmitting: mutation.isPending,
    error: mutation.error,
    submit: async (allowedEndpoints: string[]) => {
      return await mutation.mutateAsync(allowedEndpoints);
    },
    reset: () => {
      setDisplayName('');
      setScopes([]);
      setLimited(false);
      setPickedEndpoints([]);
      mutation.reset();
    },
  };
};

export const useRevokeApiKeyLogic = (tenantId: string | undefined): RevokeApiKeyLogic => {
  const queryClient = useQueryClient();

  const mutation = useMutation<void, Error, string>({
    mutationFn: (keyId) =>
      apiRequest<ApiKey>(`/api/api-keys/${keyId}/revoke`, {
        method: 'PATCH',
      }).then(() => undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys(tenantId ?? '') });
    },
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    revoke: async (keyId: string) => {
      await mutation.mutateAsync(keyId);
    },
  };
};

export const useDeleteApiKeyLogic = (tenantId: string | undefined): DeleteApiKeyLogic => {
  const queryClient = useQueryClient();

  const mutation = useMutation<void, Error, string>({
    mutationFn: (keyId) =>
      apiRequest<void>(`/api/api-keys/${keyId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys(tenantId ?? '') });
    },
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    delete: async (keyId: string) => {
      await mutation.mutateAsync(keyId);
    },
  };
};

interface ApiKeyEndpointsLogic {
  endpoints: AppEndpoint[];
  isLoading: boolean;
  error: Error | null;
}

/** The endpoints a key can be limited to, each with the scope that guards it. */
export const useApiKeyEndpointsLogic = (tenantId: string | undefined): ApiKeyEndpointsLogic => {
  const query = useQuery<AppEndpointListResponse>({
    queryKey: queryKeys.apiKeyEndpoints(tenantId ?? ''),
    queryFn: () => apiRequest<AppEndpointListResponse>(`/api/api-keys/endpoints`),
    enabled: Boolean(tenantId),
  });

  return {
    endpoints: query.data?.endpoints ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
};

interface UpdateApiKeyAccessLogic {
  isSubmitting: boolean;
  error: Error | null;
  updateAccess: (keyId: string, scopes: string[], allowedEndpoints: string[]) => Promise<void>;
}

/**
 * Saves a key's scopes and endpoint limit in one request, so the server checks
 * and stores them together. Full replace; an empty endpoint list removes the limit.
 */
export const useUpdateApiKeyAccessLogic = (
  tenantId: string | undefined,
): UpdateApiKeyAccessLogic => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    ApiKey,
    Error,
    { keyId: string; scopes: string[]; allowedEndpoints: string[] }
  >({
    mutationFn: ({ keyId, scopes, allowedEndpoints }) =>
      apiRequest<ApiKey>(`/api/api-keys/${keyId}/scopes`, {
        method: 'PATCH',
        body: { scopes, allowedEndpoints },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys(tenantId ?? '') });
    },
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    updateAccess: async (keyId: string, scopes: string[], allowedEndpoints: string[]) => {
      await mutation.mutateAsync({ keyId, scopes, allowedEndpoints });
    },
  };
};
